import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLevumiItemMath } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemMath.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import '@chancenwerk/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { filter, switchMap, takeUntil } from 'rxjs';

@customElement('campus-dialog-edit-levumi-math-item')
export class CampusDialogEditLevumiMathItem extends ChromiumDialog<void> {
  static override get element_name() {
    return 'campus-dialog-edit-levumi-math-item';
  }

  @state() public levumiMathItem = new RXController(this, new CampusLevumiItemMath({}));
  @state() private readonly _itemDirty = new RXController(
    this,
    this.levumiMathItem.observable.pipe(
      filter(Boolean),
      switchMap(levumiMathItem => levumiMathItem.dirty),
    ),
  );

  @state() private readonly _levumiLevels = new RXController(
    this,
    LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.MATH)(CampusLevumiLevel.allFields),
  );

  private readonly _form = new FormController(this, this.levumiMathItem.observable, {
    success: async mathItem => this._handleFormSuccess(mathItem),
    error: err => this._handleFormError(err),
  });

  @state() private readonly _errorMsg = new RXController<string>(this, '');

  override connectedCallback() {
    super.connectedCallback();
    this.levumiMathItem.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => (this.heading = !e._id ? 'Levumi Mathe Aufgabe erstellen' : 'Levumi Mathe Aufgabe bearbeiten'));
  }

  private async _autoCorrectCategory(levumiMathItem: CampusLevumiItemMath) {
    // Automatically set Level based on Functions in them

    const structure = {
      sumSubs: {
        specifier: 'Subtraktion',
        number: (levumiMathItem.mathFunction.match(/-/g) ?? []).length,
        category: LevumiMathCategory.SUB,
      },
      sumAdds: {
        specifier: 'Addition',
        number: (levumiMathItem.mathFunction.match(/\+/g) ?? []).length,
        category: LevumiMathCategory.ADD,
      },
      sumMuls: {
        specifier: 'Multiplikation',
        number: (levumiMathItem.mathFunction.match(/\*/g) ?? []).length,
        category: LevumiMathCategory.MUL,
      },
      sumDiv: {
        specifier: 'Division',
        number: (levumiMathItem.mathFunction.match(/\//g) ?? []).length,
        category: LevumiMathCategory.DIV,
      },
    };

    for (const structurePoint of Object.keys(structure)) {
      const currentOperator = structure[structurePoint];
      if (
        currentOperator.number &&
        (await ChromiumDialog.confirm(
          `Die Aufgabe beinhaltet eine ${currentOperator.specifier as string}. Soll die Kategorie automatisch auf ${
            currentOperator.specifier as string
          } gestellt werden?`,
          { acceptText: 'Ja', declineText: 'Nein' },
        ))
      ) {
        this.levumiMathItem.value.category = currentOperator.category;
      } else {
        return false;
      }
    }
  }

  private async _handleFormSuccess(levumiMathItem: CampusLevumiItemMath) {
    levumiMathItem.mathFunction = levumiMathItem.mathFunction.replace(/ /g, '');

    // Possible switch of level reactivity fix
    const newLevel = this._levumiLevels.value?.find(l => l._id === levumiMathItem.levelID);
    if (newLevel) {
      this.levumiMathItem.value.level = newLevel;
    }

    if (!this._validateItem(levumiMathItem)) return false;

    // Autocorrect Category
    await this._autoCorrectCategory(levumiMathItem);

    if (!levumiMathItem._id) await levumiMathItem.create().catch(e => this._errorMsg.next(e.detail));
    else await levumiMathItem.save().catch(e => this._errorMsg.next(e.detail));
    void ChromiumSnackbar.show('Funktion gespeichert');
    await this.close();
  }
  private _handleFormError(err: ValidationError<any>) {
    this._errorMsg.next(err.message);
  }

  override content() {
    return html`
      <cr-flex space="md" padding="md">
        ${this._errorMsg.value.length
          ? html` <cr-text ${css({ color: 'red' })}>${this._errorMsg.value}</cr-text>`
          : nothing}
        <cr-text> Das Format einer Aufgabe ist wie folgt: 12 + 7 = $ oder $ + 43 = 64</cr-text>
        <cr-text> Leerzeichen können ignoriert werden</cr-text>
        <cr-text> Die verfügbaren Operatoren sind: + - * /</cr-text>
        <cr-dropdown-selection
          placeholder="Kategorie"
          ${this._form.field('category')}
          .selectables=${Object.values(LevumiMathCategory)}
          hide-add-button
        ></cr-dropdown-selection>
        <cr-flex horizontal space="md" crossaxis-end>
          <cr-dropdown-selection
            hide-add-button
            placeholder="Station"
            ${this._form.field('levelID')}
            .selectables=${this._filterStations(this._levumiLevels.value ?? [])
              .sort((a, b) => (a.level.trim().toUpperCase() < b.level.trim().toUpperCase() ? -1 : 1))
              .map(l => l._id)}
            .renderString=${(levelID: string) => this._levumiLevels.value?.find(lev => lev._id === levelID)?.level}
            .renderItem=${(levelID: string) => {
              const level = this._levumiLevels.value?.find(lev => lev._id === levelID);
              if (!level || level.level === 'unspecified') return;
              return html`
                <cr-flex
                  center
                  crossaxis-center
                  horizontal
                  space="md"
                  padding="md"
                  ${css({ borderTop: `1px solid #f2f2f2`, cursor: 'pointer' })}
                >
                  <cr-text>${level.level}</cr-text>
                  <cr-button
                    mode="text"
                    icon="delete"
                    tone="error"
                    @click=${async e => {
                      e.stopPropagation();
                      await this._deleteLevel(level);
                    }}
                  >
                    Löschen
                  </cr-button>
                </cr-flex>
              `;
            }}
            flex
          ></cr-dropdown-selection>

          <cr-button
            icon="add"
            @click=${async e => {
              const levelName = await ChromiumDialog.prompt('Name für die Station eintragen', {
                placeholder: 'Stationsname',
              });
              if (!levelName && this._levumiLevels.value?.some(val => val.level === levelName)) return;
              await new CampusLevumiLevel({
                level: levelName,
                subject: LevumiSubject.MATH,
              }).create();
              void ChromiumSnackbar.show(levelName + ' wurde für Mathe gespeichert');
            }}
          >
          </cr-button>
        </cr-flex>

        <cr-input type="number" ${this._form.field('difficulty')} placeholder="Schwierigkeitsgrad"> </cr-input>

        <cr-input ${this._form.field('mathFunction')} placeholder="*Funktion (Bsp: 1 + $ = 3)"></cr-input>
        <cr-flex horizontal crossaxis-center end space="md">
          ${this.levumiMathItem.value._id
            ? html`
                <campus-delete-button
                  @click="${async () => {
                    if (
                      !(await ChromiumDialog.confirm('Levumi Mathe Item wirklich löschen?', { highlightDecline: true }))
                    )
                      return;
                    await this.levumiMathItem.value.remove();
                    await this.close();
                  }}"
                >
                </campus-delete-button>
              `
            : nothing}
          <cr-button icon="save" @click=${() => this._form.validate()}>
            ${!this.levumiMathItem.value._id ? html` Erstellen ` : html` Speichern `}
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _filterStations(levels: CampusLevumiLevel[]) {
    return levels.filter(l => {
      if (this.levumiMathItem.value.category !== LevumiMathCategory.MUL && l.level === 'Station 6') return false;
      if (this.levumiMathItem.value.category !== LevumiMathCategory.MUL && l.level === 'Station 5') return false;
      return true;
    });
  }

  private async _deleteLevel(level: CampusLevumiLevel) {
    const levelIsUsed = await LearningContentAPI.levumi.getLevelCurrentlyInUse(level._id)();
    if (levelIsUsed) {
      if (
        await ChromiumDialog.confirm(
          html`
            <cr-flex center padding="md" space="md" space-between crossaxis-center>
              <cr-h2 ${css({ color: 'red' })}>Achtung!</cr-h2>
              <cr-h3>
                Diese Station wird im Augenblick noch von anderen Aufgaben benutzt. Bei der Löschung werden diese
                Aufgaben zu dem Level 'unspecified' hinzugefügt. Sie sind dann nicht mehr in den jeweiligen Aufgaben
                verfügbar und müssen manuell zu einer Station hinzugefügt werden.
              </cr-h3>
            </cr-flex>
          `,
          { highlightDecline: true },
        )
      ) {
        const sameLevelAsCurrentItem = this.levumiMathItem.value.level._id === level._id;
        await LearningContentAPI.levumi.changeLevelToUnspecified(CampusLevumiItemMath._class, level._id)();
        await level.remove();
        if (sameLevelAsCurrentItem) await this.close();
      }
    } else {
      await level.remove();
    }
  }

  // These are a bunch of regex checks over the function string. There is no point to reduce complexity for this function
  // eslint-disable-next-line complexity
  private _validateItem(item: CampusLevumiItemMath) {
    // Check Wildcards
    const sumWildcards = (item.mathFunction.match(/\$/g) ?? []).length;
    if (sumWildcards !== 1) {
      this._errorMsg.next('Die Aufgabe darf bzw. muss genau eine Wildcard ($) enthalten.');
      return false;
    }

    // Check Operator
    const sumAdds = (item.mathFunction.match(/\+/g) ?? []).length;
    const sumSubs = (item.mathFunction.match(/-/g) ?? []).length;
    const sumMuls = (item.mathFunction.match(/\*/g) ?? []).length;
    const sumDivs = (item.mathFunction.match(/\//g) ?? []).length;

    // Check if any is present
    if (sumAdds !== 1 && sumSubs !== 1 && sumMuls !== 1 && sumDivs !== 1) {
      this._errorMsg.next('Die Aufgabe darf bzw. muss entweder ein (+), (-), (*) oder ein (/) enthalten.');
      return false;
    }

    // Check if only one is present
    if ([sumSubs > 0, sumAdds > 0, sumMuls > 0, sumDivs > 0].filter(Boolean).length > 1) {
      this._errorMsg.next('Levumi Mathe Aufgaben können nur eine Operation pro Frage enthalten.');
      return false;
    }

    // Check = sign
    const sumEquals = (item.mathFunction.match(/=/g) ?? []).length;
    if (sumEquals !== 1) {
      this._errorMsg.next('Die Aufgabe muss genau ein (=) enthalten.');
      return false;
    }

    // Check number and generell structure
    const digitCheck = /([0-9]*|\$)(\+|-|\*|\/)([0-9]*|\$)=([0-9]*|\$)/.test(item.mathFunction);
    if (!digitCheck) {
      this._errorMsg.next('Die Aufgabe muss genau zwei Zahlen enthalten.');
      return false;
    }
    return true;
  }

  public override async cancel() {
    if (this._itemDirty.value && this.levumiMathItem.value._id) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      this.levumiMathItem.value.revert();
    }
    await super.cancel();
  }
}
